/**
 * prescribeStates.ts — WHICH ORDER STATES A CLINICIAN MAY STILL PRESCRIBE IN.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribing truth
 * (2026-09-12).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS FILE EXISTS BECAUSE A SECOND, SHORTER LIST ALREADY COST US SOMETHING.
 *
 * orders-service allows the WHOLE post-approval tail to be prescribed into —
 * `approved`, `awaiting_dispense`, `dispensed`, `dispatched`, plus
 * `dispense_rejected` — deliberately, and its own comment says why: "a clinician
 * correcting one of those is correcting the RECORD … and they can only correct
 * what they can see."
 *
 * The portal's Decision card asked a DIFFERENT question — `state === 'approved'`
 * — and so the "Re-save the prescription" button, built for the finite v1
 * backlog of doseless prescriptions, was invisible on precisely the orders that
 * backlog consists of. The deleted auto-issue minted the doseless prescription
 * AND emailed the pharmacy in one approve click, so every one of those orders had
 * already moved on to `awaiting_dispense` or beyond before anyone noticed. Two
 * real patients are at a pharmacy with a four-fold-overstated strength and the
 * repair control was rendered on none of their cards.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A MIRROR AND NOT A READ
 *
 * The honest first choice is to not hold a list at all, and for the RE-SAVE
 * CONTROL ITSELF that is exactly what happens: `needsPrescriptionReapproval` is
 * computed on the server by `prescribingStatus()`, whose very first line is
 * `if (!isClinicianPrescribeState(state)) return { …false, …false }`. The flag
 * IS the server's answer with the state set already folded into it, the portal
 * relays it, and the button is gated on the flag (R41 — the server is the
 * control).
 *
 * What still needs a state predicate is the BRANCH AROUND it: the Decision card
 * has to decide between the post-approval surface and the approve/reject
 * surface, and asking that with `owesPrescribing(flags)` would put Approve and
 * Reject buttons on a dispensed order the moment its prescription is correct.
 * There is no shared contracts package on this portal's dependency list and no
 * endpoint that serves the set, so this is a MIRROR, and it is one file with one
 * list so that there is exactly one thing to change when the server's changes.
 *
 * ⚠️ THE SERVER FILE THIS MIRRORS:
 *     gpsclinics-orders-service — src/orders/order-states.ts
 *     `CLINICIAN_PRESCRIBE_STATES` (= `POST_APPROVAL_STATES` + `dispense_rejected`)
 *   If a state is added or removed there, change it HERE and nowhere else in
 *   this portal. A divergence is the defect above, again.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS HALF A QUESTION, ON PURPOSE. A state in this set does NOT mean an order
 * owes a prescriber anything — the server pairs it with `correctlyPrescribed`,
 * which is what makes an order LEAVE the lane the moment it is properly
 * prescribed. The portal pairs it the same way, with the two relayed flags in
 * prescribingFlags.tsx. Neither half is the whole answer alone.
 *
 * R28: no medicine name here. R19: nothing logged.
 * ═════════════════════════════════════════════════════════════════════════════
 */
import type { OrderCaseState } from '../api/types';

/**
 * Mirrors orders-service `CLINICIAN_PRESCRIBE_STATES` — see the header.
 *
 * Typed as `OrderCaseState[]` rather than `string[]` so that a value the portal
 * does not know about cannot be added here without the order-state union being
 * taught it first: a typo in this list would otherwise silently hide the control
 * on a whole state, which is the failure mode this file is about.
 */
export const CLINICIAN_PRESCRIBE_STATES: readonly OrderCaseState[] = [
  // POST_APPROVAL_STATES — approved, and everything fulfilment does afterwards.
  'approved',
  'awaiting_dispense',
  'dispensed',
  'dispatched',
  // …plus the halted case. Its money is captured and it still holds a document
  // that may need correcting, so orders lets a clinician prescribe into it even
  // though it is excluded from POST_APPROVAL_STATES for capture purposes.
  'dispense_rejected',
];

/**
 * True when this state can still carry a prescribing job.
 *
 * NOT the whole question — the caller must also consult what the server says the
 * order owes (`prescribingFlagsOf`) — and named so that reads at the call site.
 * `null`/`undefined` (a review whose order state has not loaded) answers false:
 * the post-approval surface is the one with the irreversible buttons on it, so
 * an unknown state must not open it.
 */
export function isClinicianPrescribeState(state: string | null | undefined): boolean {
  if (!state) return false;
  return (CLINICIAN_PRESCRIBE_STATES as readonly string[]).includes(state);
}
